import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { meApi } from "../../api";
import Sidebar from "../layout/Sidebar";
import classes from "./DietPlanDetail.module.scss";
import logoSrc from "../../assets/trophiaLogo.png";

// ── Types ─────────────────────────────────────────────────
type MealItem = {
  id: number; name: string; quantity?: string;
  cal: number; protein: number; fat: number; carb?: number;
};
type MealSection = {
  id: string; type: typeof MEAL_KEYS[number]; items: MealItem[];
};
type WeekPlan = { sections?: MealSection[]; clientSelections?: Record<string, number[]> };
type DietPlan = Record<string, WeekPlan>;

const WEEKS     = ["Week 1", "Week 2", "Week 3", "Week 4"];
const MEAL_KEYS = ["Breakfast", "Snack", "Lunch", "Dinner", "Supplement"] as const;
const MEAL_META: { key: typeof MEAL_KEYS[number]; label: string; emoji: string }[] = [
  { key: "Breakfast",  label: "Breakfast",  emoji: "🥣" },
  { key: "Snack",      label: "Snack",      emoji: "🍎" },
  { key: "Lunch",      label: "Lunch",      emoji: "🍗" },
  { key: "Dinner",     label: "Dinner",     emoji: "🥗" },
  { key: "Supplement", label: "Supplement", emoji: "💊" },
];

const Spinner = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
    <div style={{ width: 32, height: 32, border: "3px solid #EBF7EB", borderTopColor: "#4A7C40", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const DietPlanDetail = () => {
  const { user }   = useAuth();
  const navigate   = useNavigate();

  const [dietPlan,      setDietPlan]      = useState<DietPlan>({});
  const [unlockedWeeks, setUnlockedWeeks] = useState<string[]>(["Week 1"]);
  const [planWeeks,     setPlanWeeks]     = useState<number>(0);
  const [dietType,      setDietType]      = useState<string>("");
  const [selectedWeek,  setSelectedWeek]  = useState("Week 1");
  const [notes,         setNotes]         = useState<string>("");
  const [loading,       setLoading]       = useState(true);
  const [selectedItems, setSelectedItems] = useState<Record<string, Set<number>>>({});

  useEffect(() => {
    Promise.all([
      meApi.getDietPlan().catch(() => ({ dietPlan: {} })),
      meApi.profile().catch(() => null),
    ]).then(([dd, pd]) => {
      setDietPlan(dd.dietPlan ?? {});
      const unlocked = pd?.user?.unlockedWeeks ?? ["Week 1"];
      setUnlockedWeeks(unlocked);
      setPlanWeeks(pd?.user?.planWeeks ?? 0);
      setDietType(pd?.user?.dietType ?? "");
      if (unlocked.length) setSelectedWeek(unlocked[0]);
      setNotes(pd?.user?.notes ?? "");
    }).finally(() => setLoading(false));
  }, []);

  const weekSections: MealSection[] = dietPlan[selectedWeek]?.sections ?? [];

  // Load saved selections when week/plan changes; default all selected
  useEffect(() => {
    const saved = dietPlan[selectedWeek]?.clientSelections;
    const init: Record<string, Set<number>> = {};
    weekSections.forEach(sec => {
      init[sec.id] = saved?.[sec.id]
        ? new Set(saved[sec.id])
        : new Set(sec.items.map(i => i.id));
    });
    setSelectedItems(init);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek, dietPlan]);

  const isSelected = (sectionId: string, id: number) => selectedItems[sectionId]?.has(id) ?? true;

  const toggleItem = (sectionId: string, id: number) => {
    setSelectedItems(prev => {
      const set = new Set(prev[sectionId] ?? []);
      if (set.has(id)) set.delete(id); else set.add(id);
      const next = { ...prev, [sectionId]: set };
      const serialized: Record<string, number[]> = {};
      Object.entries(next).forEach(([k, v]) => { serialized[k] = [...v]; });
      // Update local dietPlan so week-switch doesn't lose the state
      setDietPlan(p => ({
        ...p,
        [selectedWeek]: { ...p[selectedWeek], clientSelections: serialized },
      }));
      meApi.saveSelections(selectedWeek, serialized).catch(() => {});
      return next;
    });
  };

  const totalCal     = weekSections.reduce((sum, sec) => sum + sec.items.filter(i => isSelected(sec.id, i.id)).reduce((a, i) => a + (i.cal ?? 0), 0), 0);
  const totalProtein = weekSections.reduce((sum, sec) => sum + sec.items.filter(i => isSelected(sec.id, i.id)).reduce((a, i) => a + (i.protein ?? 0), 0), 0);
  const totalFat     = weekSections.reduce((sum, sec) => sum + sec.items.filter(i => isSelected(sec.id, i.id)).reduce((a, i) => a + (i.fat ?? 0), 0), 0);

  const toDataUrl = (url: string): Promise<string> =>
    fetch(url).then(r => r.blob()).then(b => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(b);
    }));

  const handleDownload = async () => {
    const logoDataUrl = await toDataUrl(logoSrc).catch(() => "");
    const MEAL_EMOJI: Record<string, string> = { Breakfast: "🥣", Snack: "🍎", Lunch: "🍗", Dinner: "🥗", Supplement: "💊" };
    const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

    const weeksHTML = Object.entries(dietPlan).map(([week, plan]) => {
      const sections: MealSection[] = plan.sections ?? [];
      const saved = plan.clientSelections ?? {};

      const mealsHTML = sections.map(sec => {
        const selSet = saved[sec.id] ? new Set(saved[sec.id]) : new Set(sec.items.map(i => i.id));
        const items = sec.items.filter(i => selSet.has(i.id));
        if (!items.length) return "";
        const emoji = MEAL_EMOJI[sec.type] ?? "";
        const secCal  = items.reduce((s, i) => s + (i.cal     ?? 0), 0);
        const secProt = items.reduce((s, i) => s + (i.protein  ?? 0), 0);
        const secFat  = items.reduce((s, i) => s + (i.fat      ?? 0), 0);
        return `
        <div class="meal-card">
          <div class="meal-header">
            <span class="meal-emoji">${emoji}</span>
            <span class="meal-type">${sec.type}</span>
            <span class="meal-macro-tag">${secCal} kcal &nbsp;·&nbsp; P ${secProt}g &nbsp;·&nbsp; F ${secFat}g</span>
          </div>
          <ul class="food-list">
            ${items.map(o => `
            <li class="food-item">
              <span class="food-name">${o.name}${o.quantity ? `<span class="food-qty"> · ${o.quantity}</span>` : ""}</span>
              <span class="food-macros">${o.cal} kcal &nbsp; P ${o.protein}g &nbsp; F ${o.fat}g</span>
            </li>`).join("")}
          </ul>
        </div>`;
      }).filter(Boolean).join("");

      if (!mealsHTML) return "";

      const allItems = sections.flatMap(sec => {
        const selSet = saved[sec.id] ? new Set(saved[sec.id]) : new Set(sec.items.map(i => i.id));
        return sec.items.filter(i => selSet.has(i.id));
      });
      const wCal  = allItems.reduce((s, i) => s + (i.cal     ?? 0), 0);
      const wProt = allItems.reduce((s, i) => s + (i.protein  ?? 0), 0);
      const wFat  = allItems.reduce((s, i) => s + (i.fat      ?? 0), 0);

      return `
      <div class="week-section">
        <div class="week-header">
          <span class="week-label">${week}</span>
          <span class="week-totals">${wCal} kcal &nbsp;·&nbsp; Protein ${wProt}g &nbsp;·&nbsp; Fat ${wFat}g</span>
        </div>
        <div class="meals-grid">${mealsHTML}</div>
      </div>`;
    }).filter(Boolean).join("");

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>Diet Plan — Trophia</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1F2A1F; background: #fff; padding: 32px 36px; }

  /* Header */
  .doc-header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 20px; border-bottom: 2px solid #EBF7EB; margin-bottom: 28px; }
  .doc-logo { height: 38px; object-fit: contain; }
  .doc-meta { text-align: right; }
  .doc-title { font-size: 20px; font-weight: 700; color: #1F2A1F; }
  .doc-sub { font-size: 12px; color: #7A8C7A; margin-top: 3px; }
  .diet-badge { display: inline-block; background: #EBF7EB; color: #4A7C40; border: 1px solid #A8D5A2; border-radius: 20px; font-size: 11px; font-weight: 600; padding: 2px 10px; margin-top: 5px; }

  /* Week */
  .week-section { margin-bottom: 32px; page-break-inside: avoid; }
  .week-header { display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #4A7C40, #5a9450); color: white; border-radius: 10px; padding: 10px 16px; margin-bottom: 14px; }
  .week-label { font-size: 14px; font-weight: 700; }
  .week-totals { font-size: 11px; opacity: 0.9; }

  /* Meals grid */
  .meals-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

  /* Meal card */
  .meal-card { border: 1.5px solid #E6EDE6; border-radius: 12px; overflow: hidden; }
  .meal-header { display: flex; align-items: center; gap: 8px; padding: 10px 14px 8px; background: #F8FBF8; border-bottom: 1px solid #E6EDE6; }
  .meal-emoji { font-size: 16px; flex-shrink: 0; }
  .meal-type { font-size: 13px; font-weight: 700; color: #1F2A1F; flex: 1; }
  .meal-macro-tag { font-size: 10px; color: #4A7C40; font-weight: 600; white-space: nowrap; }

  /* Food list */
  .food-list { list-style: none; padding: 8px 0; }
  .food-item { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding: 5px 14px; font-size: 12px; border-bottom: 1px solid #F0F5F0; }
  .food-item:last-child { border-bottom: none; }
  .food-name { color: #1F2A1F; flex: 1; }
  .food-qty { color: #8A9E8A; font-weight: 400; }
  .food-macros { color: #7A8C7A; font-size: 11px; white-space: nowrap; flex-shrink: 0; }

  /* Footer */
  .doc-footer { margin-top: 36px; padding-top: 14px; border-top: 1px solid #E6EDE6; display: flex; align-items: center; justify-content: space-between; font-size: 11px; color: #aaa; }

  @media print {
    body { padding: 20px 24px; }
    .week-section { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="doc-header">
    ${logoDataUrl ? `<img class="doc-logo" src="${logoDataUrl}" alt="Trophia" />` : `<span style="font-size:20px;font-weight:800;color:#4A7C40;">Trophia</span>`}
    <div class="doc-meta">
      <div class="doc-title">Diet Plan — ${user?.name ?? ""}</div>
      <div class="doc-sub">Generated ${today}</div>
      ${dietType ? `<div class="diet-badge">${dietType}</div>` : ""}
    </div>
  </div>

  ${weeksHTML}

  <div class="doc-footer">
    <span>Trophia Nutrition</span>
    <span>Confidential — for ${user?.name ?? "client"} only</span>
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, "_blank");
    if (win) win.addEventListener("load", () => { win.focus(); win.print(); URL.revokeObjectURL(url); });
  };

  const hasAnyPlan = Object.keys(dietPlan).length > 0;

  return (
    <div className={classes.layout}>
      <Sidebar />

      <div className={classes.main_wrap}>
        <header className={classes.topbar}>
          <div className={classes.breadcrumb}>
            <button type="button" className={classes.breadcrumb_btn} onClick={() => navigate("/dashboard")}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              Diet Plans
            </button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
            <span className={classes.breadcrumb_current}>{selectedWeek}</span>
          </div>
          <div className={classes.topbar_right}>
            <div className={classes.avatar}>{(user?.name ?? "?")[0].toUpperCase()}</div>
          </div>
        </header>

        <main className={classes.content}>
          <div className={classes.plan_header}>
            <div className={classes.plan_header_text}>
              <h1 className={classes.plan_title}>My Diet Plan</h1>
            </div>
            <div className={classes.plan_header_badges}>
              {dietType && <span className={classes.diet_type_badge}>{dietType}</span>}
              {hasAnyPlan && (
                <span className={classes.active_badge}>
                  <span className={classes.active_dot} />Active
                </span>
              )}
            </div>
          </div>

          {loading ? <Spinner /> : !hasAnyPlan ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#999" }}>
              <p style={{ fontSize: 15 }}>No diet plan assigned yet.</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>Your dietitian will assign your plan after your first appointment.</p>
            </div>
          ) : (
            <>
              <div className={classes.actions}>
                <button type="button" className={classes.action_primary} onClick={handleDownload}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download PDF
                </button>
                <button type="button" className={classes.action_outline} onClick={handleDownload}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" />
                  </svg>
                  Print
                </button>
              </div>

              {/* Week tabs — only show weeks within planWeeks, lock if not unlocked */}
              <div className={classes.day_tabs}>
                {(planWeeks ? WEEKS.slice(0, planWeeks) : WEEKS).map(w => {
                  const isUnlocked = unlockedWeeks.includes(w);
                  return (
                    <button key={w} type="button" disabled={!isUnlocked}
                      className={`${classes.day_tab} ${selectedWeek === w ? classes.day_tab_active : ""} ${!isUnlocked ? classes.day_tab_locked : ""}`}
                      onClick={() => isUnlocked && setSelectedWeek(w)}
                      title={!isUnlocked ? "Your dietitian will unlock this week soon" : undefined}
                    >
                      {w}
                      {!isUnlocked && (
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 5, flexShrink: 0 }}>
                          <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Totals */}
              {weekSections.length > 0 && (
                <div className={classes.macro_summary}>
                  <div className={classes.macro_summary_label}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4A7C40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <strong>{selectedWeek} — Daily totals</strong>
                  </div>
                  <div className={classes.macro_pills}>
                    <span className={classes.macro_pill}>
                      <span className={classes.macro_pill_label}>Calories</span>
                      <span className={classes.macro_pill_value}>{totalCal} kcal</span>
                    </span>
                    <span className={classes.macro_pill}>
                      <span className={classes.macro_pill_label}>Protein</span>
                      <span className={classes.macro_pill_value}>{totalProtein} g</span>
                    </span>
                    <span className={classes.macro_pill}>
                      <span className={classes.macro_pill_label}>Fat</span>
                      <span className={classes.macro_pill_value}>{totalFat} g</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Meal sections */}
              {weekSections.length === 0 ? (
                <p style={{ color: "#999", fontSize: 13, padding: "32px 0", textAlign: "center" }}>
                  No meals assigned for this week yet.
                </p>
              ) : (
                <div className={classes.meal_grid}>
                  {weekSections.map(sec => {
                    const MEAL_EMOJI: Record<string, string> = { Breakfast: "🥣", Snack: "🍎", Lunch: "🍗", Dinner: "🥗", Supplement: "💊" };
                    const emoji = MEAL_EMOJI[sec.type] ?? "";
                    if (!sec.items.length) return null;
                    return (
                      <div key={sec.id} className={classes.meal_section_card}>
                        <div className={classes.meal_section_header}>
                          <div className={classes.meal_section_title}>
                            <span className={classes.meal_section_emoji}>{emoji}</span>
                            <strong>{sec.type}</strong>
                          </div>
                          {(
                            <span className={classes.meal_section_selected}>
                              {sec.items.reduce((s, i) => s + (i.cal ?? 0), 0)} kcal · {sec.items.reduce((s, i) => s + (i.protein ?? 0), 0)}g P
                            </span>
                          )}
                        </div>
                        <div className={classes.option_list}>
                          {sec.items.map((item, i) => {
                            const sel = isSelected(sec.id, item.id);
                            return (
                              <div key={i} className={`${classes.food_item_wrap} ${!sel ? classes.meal_item_deselected : ""}`}>
                                {item.name && (
                                  <div className={classes.food_row_label}>
                                    {item.name}{item.quantity ? <span className={classes.food_qty_inline}> · {item.quantity}</span> : null}
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className={classes.meal_item_row}
                                  onClick={() => toggleItem(sec.id, item.id)}
                                >
                                  <span className={classes.item_check}>
                                    {sel
                                      ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4A7C40" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                      : <span className={classes.item_check_empty}/>
                                    }
                                  </span>
                                  <div className={classes.option_macros}>
                                    <span>{item.cal} kcal</span>
                                    <span>{item.protein}g P</span>
                                    <span>{item.fat}g F</span>
                                    {item.carb != null && <span>{item.carb}g C</span>}
                                  </div>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Right panel */}
      <aside className={classes.right_panel}>
        <div className={classes.panel_card}>
          <div className={classes.panel_card_title}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4A7C40" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" />
              <line x1="9" y1="12" x2="15" y2="12" /><line x1="9" y1="16" x2="13" y2="16" />
            </svg>
            Notes from Your Dietitian
          </div>
          {notes ? (
            <p style={{ fontSize: 13, color: "#444", lineHeight: 1.6, whiteSpace: "pre-wrap", margin: 0 }}>{notes}</p>
          ) : (
            <p style={{ fontSize: 13, color: "#999", padding: "8px 0", margin: 0 }}>Your dietitian's notes will appear here.</p>
          )}
        </div>
      </aside>
    </div>
  );
};

export default DietPlanDetail;
